import {
	forwardRef,
	createContext,
	useContext,
	useMemo,
	HTMLAttributes,
} from "react"
import classNames from "classnames"
import { Box, BoxProps } from "./Box"
import { tableClassName, tableVariants } from "./table.css"
import Icons from "./Icons"

export interface TableProps extends BoxProps<"table"> {
	/**
	 * Table has a fixed layout
	 * @default false
	 */
	fixedLayout?: boolean
	/**
	 * Headers for the table
	 * @default undefined
	 */
	headers?: React.ReactNode[]
	/**
	 * Rows in the table
	 * @default undefined
	 */
	rows?: React.ReactNode[][]
	/**
	 * Keys for the rows in the table. If not provided, index will be used as key
	 * @default undefined
	 */
	rowKeys?: (string | number)[]
	/**
	 * Align the columns on right or center. Pass the column number(index) as key and right | center as it's value in the object
	 * @default undefined
	 */
	alignCols?: { [key: number]: "center" | "right" }
	/**
	 * Highlight specific header columns (e.g. the currently active sort column).
	 * Pass the column number(index) as key and true as it's value in the object
	 * @default undefined
	 */
	highlightCols?: { [key: number]: boolean }
	/**
	 * Does the table requires responsiveness?
	 * @default false
	 */
	responsive?: boolean
	/**
	 * Caption for the table
	 * @default ""
	 */
	caption?: string
	/**
	 * Width Auto
	 */
	autoWidth?: boolean
	/**
	 * Add zebra-striping
	 */
	striped?: boolean
	/**
	 * Add border around each data cell
	 */
	bordered?: boolean
	/**
	 * Change row's background color when hovered
	 */
	hover?: boolean

	/**
	 * Background color applied to all header (th) cells.
	 * Accepts any Box bgColor value (e.g. "subtle", "inset", "primary", "default").
	 * @default undefined
	 */
	headerBgColor?: BoxProps<"th">["bgColor"]

	/**
	 * Vertical alignment for header (th) cells.
	 * @default "bottom"
	 */
	headerVerticalAlign?: BoxProps<"th">["verticalAlign"]

	/**
	 * Stick the header to the top
	 */
	stickyHeader?: boolean
	stickyFooter?: boolean

	/**
	 * Stick the header to the top
	 */
	stickyFirstColumn?: boolean
	stickyLastColumn?: boolean

	/**
	 * Callback to provide pre-row HTML attributes (e.g style) based on the row index
	 */
	rowProps?: (rowIndex: number) => HTMLAttributes<HTMLTableRowElement>
}

const TableContext = createContext<{
	fixedLayout: boolean
	striped: boolean
	bordered: boolean
	hover: boolean
	responsive: boolean
	headerBgColor?: BoxProps<"th">["bgColor"]
}>({
	fixedLayout: false,
	striped: false,
	bordered: false,
	hover: false,
	responsive: false,
	headerBgColor: undefined,
})

function useTableContext() {
	return useContext(TableContext)
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
	(
		{
			fixedLayout,
			className,
			headers,
			children,
			alignCols = {},
			highlightCols = {},
			rows,
			rowKeys,
			responsive,
			caption,
			autoWidth,
			striped,
			bordered,
			hover,
			headerBgColor,
			headerVerticalAlign,
			stickyHeader,
			stickyFooter,
			stickyFirstColumn,
			stickyLastColumn,
			style,
			rowProps,
			...otherProps
		},
		ref
	) => {
		const context = useMemo(() => {
			return {
				fixedLayout: fixedLayout || false,
				striped: striped || false,
				bordered: bordered || false,
				hover: hover || false,
				responsive: responsive || false,
				headerBgColor,
			}
		}, [fixedLayout, striped, bordered, hover, responsive, headerBgColor])
		const $table = (
			<TableContext.Provider value={context}>
				<Box<"table">
					as="table"
					cellSpacing={0}
					position={stickyFirstColumn || stickyHeader ? "relative" : undefined}
					ref={ref}
					width="full"
					className={classNames(
						tableClassName,
						{
							[tableVariants.fixedLayout]: fixedLayout,
							[tableVariants.autoWidth]: autoWidth ? true : undefined,
							[tableVariants.striped]: striped ? true : undefined,
							[tableVariants.bordered]: bordered ? true : undefined,
							[tableVariants.hover]: hover ? true : undefined,
							[tableVariants.stickyHeader]: stickyHeader ? true : undefined,
							[tableVariants.stickyFooter]: stickyFooter ? true : undefined,
							[tableVariants.stickyFirstColumn]: stickyFirstColumn
								? true
								: undefined,
							[tableVariants.stickyLastColumn]: stickyLastColumn
								? true
								: undefined,
						},
						className
					)}
					{...otherProps}
					style={responsive ? undefined : style}
				>
					{caption ? (
						<Box as="caption" textAlign="left" paddingY="2" paddingX="0">
							{caption}
						</Box>
					) : null}
					{headers ? (
						<thead>
							<tr>
								{headers.map((header, h) => (
									<TableHeaderDataCell
										key={h}
										textAlign={alignCols[h]}
										verticalAlign={headerVerticalAlign || "bottom"}
										bgColor={highlightCols[h] ? "warning" : undefined}
									>
										{header}
									</TableHeaderDataCell>
								))}
							</tr>
						</thead>
					) : null}
					{rows ? (
						<tbody>
							{rows.map((row, r) => {
								const extraRowProps = rowProps?.(r)
								return (
									<tr
										key={rowKeys ? rowKeys[r] : r}
										style={{ ...extraRowProps?.style }}
									>
										{row.map((data, d) => (
											<TableDataCell as="td" key={d} textAlign={alignCols[d]}>
												{data}
											</TableDataCell>
										))}
									</tr>
								)
							})}
						</tbody>
					) : null}
					{children}
				</Box>
			</TableContext.Provider>
		)
		if (responsive) {
			return (
				<Box
					display="block"
					width="full"
					maxWidth="full"
					overflowX="auto"
					style={style}
				>
					{$table}
				</Box>
			)
		}
		return $table
	}
)
Table.displayName = "Table"

export function TableHeaderDataCell(props: BoxProps<"th">) {
	const { responsive, headerBgColor } = useTableContext()
	return (
		<TableDataCell
			as="th"
			whiteSpace={responsive ? "preserve" : undefined}
			paddingY="2"
			verticalAlign="bottom"
			borderBottomWidth="1"
			fontWeight="semibold"
			bgColor={headerBgColor}
			{...props}
		/>
	)
}

export function TableFooterDataCell(props: BoxProps<"td" | "th">) {
	const { responsive } = useTableContext()
	return (
		<TableDataCell
			whiteSpace={responsive ? "preserve" : undefined}
			paddingY="2"
			verticalAlign="top"
			borderTopWidth="2"
			borderColor="emphasis"
			fontWeight="semibold"
			{...props}
		/>
	)
}

export function TableDataCell(props: BoxProps<"td" | "th">) {
	return (
		<Box
			verticalAlign="top"
			textAlign="left"
			fontVariantNumeric="tabularNums"
			paddingX="4"
			paddingY="3"
			as="td"
			{...props}
		/>
	)
}

export type TSortOrder = "asc" | "desc"

type TSortableHeaderProps<TSortKey extends string> = {
	label: string
	sortKey: TSortKey
	currentSortBy?: TSortKey
	currentSortOrder?: TSortOrder
	isValidating?: boolean
	/**
	 * The fixed sort direction to display when alwaysSorted is true.
	 * @default "desc"
	 */
	sortDirection?: TSortOrder
} & (
	| {
			/**
			 * When true, the column displays a fixed sort indicator
			 * and is not interactive.
			 */
			alwaysSorted: true
			/**
			 * Not used when alwaysSorted is true.
			 */
			onSort?: never
	  }
	| {
			/**
			 * When false or omitted, the column is interactive
			 * and supports sorting.
			 */
			alwaysSorted?: false
			/**
			 * Called when the user changes the sort.
			 */
			onSort: (
				sortBy: TSortKey | undefined,
				sortOrder: TSortOrder | undefined
			) => void
	  }
)

export function SortableHeader<TSortKey extends string>({
	label,
	sortKey,
	currentSortBy,
	currentSortOrder,
	onSort,
	isValidating,
	alwaysSorted,
	sortDirection = "desc",
}: TSortableHeaderProps<TSortKey>) {
	if (alwaysSorted) {
		return (
			<Box as="span" alignItems="center">
				{label}{" "}
				{sortDirection === "asc" ? (
					<Icons.SortAsc size="4" color="accent" />
				) : (
					<Icons.SortDesc size="4" color="accent" />
				)}
			</Box>
		)
	}
	const isActive = currentSortBy === sortKey
	const handleClick = () => {
		if (!isActive) {
			onSort(sortKey, "desc")
		} else if (currentSortOrder === "desc") {
			onSort(sortKey, "asc")
		} else {
			onSort(undefined, undefined)
		}
	}
	return (
		<Box as="span" cursor="pointer" onClick={handleClick} alignItems="center">
			{label}{" "}
			{isValidating ? (
				<Icons.Refresh rotate="180" color="muted" spin />
			) : isActive && currentSortOrder === "asc" ? (
				<Icons.SortAsc size="4" color="accent" />
			) : isActive && currentSortOrder === "desc" ? (
				<Icons.SortDesc size="4" color="accent" />
			) : (
				<Icons.Sorting size="4" color="muted" />
			)}
		</Box>
	)
}
